"use client";

import { useRef, useState } from "react";
import type { ExamMarkConfig } from "@/lib/exams/markConfig";
import { buildNaplanYearPref, NAPLAN_YEAR_PREF_KEY } from "@/lib/exams/naplan/schoolYear";
import { saveUserPref } from "@/lib/userPrefs";

const NAPLAN_GENRES = [
  { value: "persuasive", label: "Persuasive" },
  { value: "narrative", label: "Narrative" },
] as const;

const NAPLAN_YEARS = [3, 5, 7, 9] as const;

export type NaplanGenre = (typeof NAPLAN_GENRES)[number]["value"];
export type NaplanYear = (typeof NAPLAN_YEARS)[number];

// ---------------------------------------------------------------------------
// Selector state
// ---------------------------------------------------------------------------
/** NAPLAN's mark-form selector state: genre + year level + how we know the year. */
export interface NaplanSelState {
  genre: NaplanGenre;
  year: NaplanYear;
  /**
   * Provenance of `year` — drives the UI's honesty: "profile" renders a quiet
   * confirmation ("from your profile"), "user" a plain confirmation, and
   * "default" keeps the picker OPEN, because a guess must read as a question,
   * not a fact. Not part of the handoff.
   */
  yearSource?: "profile" | "user" | "default";
}

interface NaplanMarkContextProps {
  genre: NaplanGenre;
  year: NaplanYear;
  yearSource: NaplanSelState["yearSource"];
  onGenreChange: (genre: NaplanGenre) => void;
  onYearChange: (year: NaplanYear) => void;
}

/**
 * Genre + Year selectors for NAPLAN marking. Genre is a real selector (it
 * varies per piece). The year is a known-or-asked fact: known (profile-derived
 * or user-confirmed) renders collapsed with a "change" link; unknown renders
 * the picker open. The expanded picker is a BUTTON row, not a <select> — a
 * select can't collapse on re-picking the same value (no change event fires),
 * commits on the first arrow key while closed, and doesn't open on programmatic
 * focus on iOS. Buttons have none of those traps: tapping the current year is
 * an explicit confirmation (collapses + remembers), and Escape also collapses.
 */
function NaplanMarkContext({
  genre,
  year,
  yearSource,
  onGenreChange,
  onYearChange,
}: NaplanMarkContextProps) {
  const [editingYear, setEditingYear] = useState(yearSource === "default");
  const changeBtnRef = useRef<HTMLButtonElement>(null);
  // An unknown year must stay a question — if provenance degrades to "default"
  // (e.g. a state reset), reopen the picker. This is React's documented
  // adjust-state-when-a-prop-changes pattern: a render-phase setState guarded
  // by a prev-value state, not an effect and not a ref.
  const [prevSource, setPrevSource] = useState(yearSource);
  if (prevSource !== yearSource) {
    setPrevSource(yearSource);
    if (yearSource === "default" && !editingYear) setEditingYear(true);
  }

  const pick = (y: NaplanYear) => {
    onYearChange(y);
    setEditingYear(false);
    // Hand focus back to the control that opened the picker (it re-renders in
    // the same spot), so keyboard users aren't dumped at the top of the page.
    requestAnimationFrame(() => changeBtnRef.current?.focus());
  };

  return (
    <div className="flex flex-wrap gap-3">
      <div className="min-w-[160px] flex-1">
        <label htmlFor="naplan-genre" className="mb-1 block text-sm font-medium text-gray-700">
          Genre
        </label>
        <select
          id="naplan-genre"
          value={genre}
          onChange={(e) => onGenreChange(e.target.value as NaplanGenre)}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-brand focus:outline-none"
        >
          {NAPLAN_GENRES.map((g) => (
            <option key={g.value} value={g.value}>
              {g.label}
            </option>
          ))}
        </select>
      </div>
      <div className="min-w-[200px] flex-1">
        <span id="naplan-year-label" className="mb-1 block text-sm font-medium text-gray-700">
          Year level
        </span>
        {editingYear ? (
          <div
            role="group"
            aria-labelledby="naplan-year-label"
            className="flex gap-1.5"
            onKeyDown={(e) => {
              if (e.key === "Escape" && yearSource !== "default") {
                setEditingYear(false);
                requestAnimationFrame(() => changeBtnRef.current?.focus());
              }
            }}
          >
            {NAPLAN_YEARS.map((y) => (
              <button
                key={y}
                type="button"
                onClick={() => pick(y)}
                aria-pressed={y === year}
                className={`flex-1 rounded-lg border px-2 py-2 text-sm font-semibold ${
                  y === year
                    ? "border-brand bg-brand-surface text-brand"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Year {y}
              </button>
            ))}
          </div>
        ) : (
          <p className="py-2 text-sm text-gray-700">
            Year {year}
            {yearSource === "profile" && (
              <span className="ml-1 text-xs text-gray-400">(from your profile)</span>
            )}{" "}
            <button
              ref={changeBtnRef}
              type="button"
              aria-label="Change year level"
              onClick={() => setEditingYear(true)}
              className="-my-1 rounded px-2 py-1 text-xs font-semibold text-brand hover:underline"
            >
              change
            </button>
          </p>
        )}
      </div>
    </div>
  );
}

/** Build the prefix string that gets prepended to promptContext for NAPLAN. */
export function buildNaplanPromptPrefix(genre: NaplanGenre, year: NaplanYear): string {
  return `Genre: ${genre}\nYear level: ${year}\n`;
}

/** `{ state, onChange, uid }` adapter around NaplanMarkContext for ExamMarkConfig. */
function NaplanSelectors({
  state,
  onChange,
  uid,
}: {
  state: NaplanSelState;
  onChange: (next: NaplanSelState) => void;
  uid: string | null;
}) {
  // No mount-time restore here: the saved override lives on the PROFILE
  // (user_profiles.prefs) and the server resolves it into initialSelState
  // before render — see ExamModule.deriveMarkSelState.
  return (
    <NaplanMarkContext
      genre={state.genre}
      year={state.year}
      yearSource={state.yearSource ?? "user"}
      onGenreChange={(genre) => onChange({ ...state, genre })}
      onYearChange={(year) => {
        // Picking a year — including re-picking the current one — is an
        // explicit confirmation. Persist it to the profile (stamped with the
        // school year, so it self-expires) and mark it user-sourced. Fire and
        // forget: the local state is already correct, and a failed save only
        // costs the cross-device memory.
        if (uid) {
          void saveUserPref(uid, NAPLAN_YEAR_PREF_KEY, buildNaplanYearPref(year)).catch(() => {});
        }
        onChange({ ...state, year, yearSource: "user" });
      }}
    />
  );
}

/**
 * NAPLAN mark-form config. NAPLAN is a PASTE-PROMPT exam: the learner pastes the
 * task and these selectors add genre + year CONTEXT to the marker's rubric (not
 * the task itself) — hence `pastePrompt: true` and `promptText: () => null`. The
 * genre/year handoff + prefix run through the same generic path as IELTS/TOEFL/
 * Cambridge, so mark/practice/detail carry no `examId === "naplan"` branches.
 */
export const naplanMarkConfig: ExamMarkConfig<NaplanSelState> = {
  defaultState: { genre: "persuasive", year: 5, yearSource: "default" },
  pastePrompt: true,
  Selectors: NaplanSelectors,
  promptText: () => null,
  handoffKeys: ["naplanGenre", "naplanYear"],
  buildHandoff: (s) => ({ naplanGenre: s.genre, naplanYear: String(s.year) }),
  buildPrefix: (h, base) =>
    h.naplanGenre && h.naplanYear
      ? buildNaplanPromptPrefix(h.naplanGenre as NaplanGenre, Number(h.naplanYear) as NaplanYear) +
        base
      : base,
};
